import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import { Input } from '@components/core-ui/form-elements/Input';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { useAPI } from '@providers/useAPI';
import { AdminService, PrismaTransactionDto } from 'app/API';
import React, { useEffect, useState } from 'react';
import { CellContent } from './AdminRefundJobModal/styles/CellContent';
import { Content } from './AdminRefundJobModal/styles/Content';

type TProps = {
  onCancel: () => void;
  onSuccess: () => void;
  id: string;
};

export const AdminRefundJobModal = ({ onCancel, onSuccess, id }: TProps) => {
  const { call } = useAPI();
  const [isLoading, setIsLoading] = useState(false);
  const [shouldLockJob, setShouldLockJob] = useState(false);
  const [price, setPrice] = useState(0);
  const [jobTitle, setJobTitle] = useState('Loading...');
  const [isASOneJob, setIsASOneJob] = useState(false);
  const [reason, setReason] = useState('');
  const [transactionIds, setTransactionIds] = useState<string[]>([]);
  const [transactions, setTransactions] = useState<PrismaTransactionDto[]>([]);

  const isSubmitDisabled = !reason.length || !transactionIds.length || !price;

  const load = async () => {
    setIsLoading(true);
    const job = await call(AdminService.getJobById({ jobId: id }));
    setJobTitle(job.name || 'No title');
    if (job.meetingId) {
      setIsASOneJob(true);
      setShouldLockJob(true);
    }
    const transactions = await call(AdminService.getJobTransactions({ jobId: id }));
    setTransactions(transactions);
    setIsLoading(false);
  };

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    await call(AdminService.refundJob({ jobId: id, requestBody: { reason, transactionIds, shouldLockJob } }));
    setIsLoading(false);
    onSuccess();
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <ModalWrapper width="500px">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Refund Job</ModalTitle>
      <Content>
        <div className="content">
          <div className="input-wrapper">
            <div className="label">Job Title</div>
            <p>{jobTitle || 'Unnamed'}</p>
          </div>

          <div className="input-wrapper">
            <div className="label">Amount</div>
            <p>${(price / 100).toFixed(2)}</p>
          </div>

          <div className="input-wrapper reason">
            <div className="label">Reason</div>
            <Input
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
              label="Type the reason of refund"
              required={true}
              value={reason}
            />
          </div>

          <TableWrapper className="custom-table">
            <Table>
              <Thead $white>
                <Tr>
                  <Th></Th>
                  <Th>Code</Th>
                  <Th>Date</Th>
                  <Th>Amount</Th>
                  <Th>Type</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(transactions || []).map((data) => (
                  <Tr key={data.id}>
                    <Td>
                      <CellContent>
                        {data.amount < 0 ? (
                          <CheckBox
                            name={`transaction-${data.id}`}
                            checked={transactionIds.includes(data.id)}
                            onChange={(v) => {
                              if (v) {
                                setTransactionIds([...transactionIds, data.id]);
                                setPrice(price + data.amount * -1);
                              } else {
                                setTransactionIds(transactionIds.filter((id) => id !== data.id));
                                setPrice(price - data.amount * -1);
                              }
                            }}
                          />
                        ) : null}
                      </CellContent>
                    </Td>
                    <Td>
                      <CellContent>{data.codeId}</CellContent>
                    </Td>
                    <Td>
                      <CellContent>{getAmericanTime(data.createdAt)}</CellContent>
                    </Td>
                    <Td>
                      <CellContent>{Math.abs(data.amount / 100).toFixed(2)}</CellContent>
                    </Td>
                    <Td>
                      <CellContent>{data.amount < 0 ? 'Charge' : 'Refund'}</CellContent>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableWrapper>

          {!isLoading && !transactions.length ? <p className="description">No transactions found</p> : null}

          {isASOneJob ? (
            <div className="input-wrapper checkbox-wrapper">
              <CheckBox name="lock-after-refund" checked={shouldLockJob} onChange={(v) => setShouldLockJob(v)} />
              <label>Lock the job after refunding</label>
            </div>
          ) : null}
        </div>
        <form onSubmit={(e) => submit(e)}>
          <div className="buttonContainer">
            <OutlinedButton type="button" onClick={onCancel}>
              Cancel
            </OutlinedButton>
            <Button type="submit" disabled={isSubmitDisabled}>
              Refund
            </Button>
          </div>
        </form>
      </Content>
    </ModalWrapper>
  );
};
